import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  ReferenceLine,
} from "recharts";
import {
  Activity, Droplets, Layers, Calculator, Brain, Loader2, Plus,
  AlertCircle, TrendingUp, Pill, ClipboardList,
} from "lucide-react";

const ACCENT = "#8B5CF6";
const BAND_COLOR = {
  Normal: "#4ADE80", "At target": "#4ADE80",
  Prediabetes: "#FBBF24", "Above target": "#FBBF24",
  High: "#FB923C",
  Severe: "#F87171", "Overt hypothyroidism": "#F87171",
  "Overt hyperthyroidism": "#F87171",
  "Subclinical hypothyroidism": "#FBBF24", "Subclinical hyperthyroidism": "#FBBF24",
  Euthyroid: "#4ADE80",
};

function Field({ label, children }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">{label}</span>
      {children}
    </label>
  );
}

export default function Endocrinology() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTabState] = useState(searchParams.get("tab") || "overview");
  const setTab = (t) => {
    setTabState(t);
    const next = new URLSearchParams(searchParams);
    next.set("tab", t);
    setSearchParams(next, { replace: true });
  };
  useEffect(() => {
    const u = searchParams.get("tab");
    if (u && u !== tab) setTabState(u);
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const [a1c, setA1c] = useState({ readings: [], summary: {} });
  const [tsh, setTsh] = useState({ readings: [], summary: {} });
  const [ref, setRef] = useState({ formulary: [], care_plans: [] });
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [a, t, r] = await Promise.all([
        api.get("/specialties/endocrinology/hba1c"),
        api.get("/specialties/endocrinology/tsh"),
        api.get("/specialties/endocrinology/reference"),
      ]);
      setA1c(a.data); setTsh(t.data); setRef(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load endocrinology");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const TABS = [
    { key: "overview", label: "Overview", icon: Activity },
    { key: "hba1c",    label: "HbA1c",    icon: Droplets },
    { key: "thyroid",  label: "Thyroid",  icon: Layers },
    { key: "risk",     label: "Risk Scores", icon: Calculator },
    { key: "twin",     label: "Digital Twin", icon: Brain },
    { key: "formulary", label: "Formulary", icon: Pill },
    { key: "careplans", label: "Care Plans", icon: ClipboardList },
  ];

  return (
    <div className="space-y-6" data-testid="endocrinology-page">
      <div className="ros-card flex items-center gap-4">
        <div className="w-12 h-12 rounded-2xl flex items-center justify-center"
             style={{ background: `${ACCENT}18`, border: `1px solid ${ACCENT}40` }}>
          <Droplets style={{ color: ACCENT }} size={22} />
        </div>
        <div className="flex-1">
          <div className="font-heading text-xl">Endocrinology</div>
          <div className="text-xs text-[#A3ACA7] mt-0.5">
            Diabetes, thyroid, and metabolic care · ADA 2026 Standards
          </div>
        </div>
        {a1c?.summary?.latest && (
          <div className="text-center">
            <div className="text-3xl font-heading font-bold"
                 style={{ color: BAND_COLOR[a1c.summary.latest.band] || "#fff" }}>
              {a1c.summary.latest.hba1c_pct}%
            </div>
            <div className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">Latest HbA1c</div>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2" data-testid="endo-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
                    data-testid={`endo-tab-${t.key}`}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      active ? "text-black" : "bg-[#1C201E] text-[#A3ACA7] hover:text-white"
                    }`}
                    style={active ? { background: ACCENT } : {}}>
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      {loading && (
        <div className="ros-card flex items-center gap-2 text-sm text-[#A3ACA7]">
          <Loader2 className="animate-spin" size={14} /> Loading…
        </div>
      )}
      {!loading && tab === "overview" && <OverviewTab a1c={a1c} tsh={tsh} />}
      {!loading && tab === "hba1c" && <Hba1cTab data={a1c} onSaved={loadAll} />}
      {!loading && tab === "thyroid" && <ThyroidTab data={tsh} onSaved={loadAll} />}
      {!loading && tab === "risk" && <RiskTab />}
      {!loading && tab === "twin" && <TwinTab />}
      {!loading && tab === "formulary" && <FormularyTab items={ref.formulary} />}
      {!loading && tab === "careplans" && <CarePlansTab plans={ref.care_plans} />}
    </div>
  );
}

/* ============ Overview ============ */
function OverviewTab({ a1c, tsh }) {
  const latestA1c = a1c?.summary?.latest;
  const latestTsh = tsh?.summary?.latest;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="ros-card" data-testid="endo-overview-a1c">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[#A3ACA7]">
          <Droplets size={12} /> Glycaemic control
        </div>
        {latestA1c ? (
          <>
            <div className="text-4xl font-heading font-bold mt-2"
                 style={{ color: BAND_COLOR[latestA1c.band] || "#fff" }}>
              {latestA1c.hba1c_pct}<span className="text-xl text-[#A3ACA7] ml-1">%</span>
            </div>
            <div className="text-sm font-semibold mt-0.5"
                 style={{ color: BAND_COLOR[latestA1c.band] || "#A3ACA7" }}>
              {latestA1c.label}
            </div>
            <div className="text-xs text-[#A3ACA7] mt-1">
              eAG {latestA1c.eag_mg_dl} mg/dL · Recorded {latestA1c.date}
            </div>
            <div className="text-sm text-white mt-2 leading-relaxed">
              {latestA1c.recommendation}
            </div>
          </>
        ) : (
          <div className="text-sm text-[#7A8480] mt-2">No HbA1c recorded yet.</div>
        )}
      </div>
      <div className="ros-card" data-testid="endo-overview-tsh">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[#A3ACA7]">
          <Layers size={12} /> Thyroid
        </div>
        {latestTsh ? (
          <>
            <div className="text-4xl font-heading font-bold mt-2"
                 style={{ color: BAND_COLOR[latestTsh.band] || "#fff" }}>
              {latestTsh.tsh_miu_l}<span className="text-xl text-[#A3ACA7] ml-1">mIU/L</span>
            </div>
            <div className="text-sm font-semibold mt-0.5"
                 style={{ color: BAND_COLOR[latestTsh.band] || "#A3ACA7" }}>
              {latestTsh.band}
            </div>
            <div className="text-sm text-white mt-2 leading-relaxed">
              {latestTsh.recommendation}
            </div>
          </>
        ) : (
          <div className="text-sm text-[#7A8480] mt-2">No TSH recorded yet.</div>
        )}
      </div>
    </div>
  );
}

/* ============ HbA1c ============ */
function Hba1cTab({ data, onSaved }) {
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    hba1c_pct: "", fasting_glucose_mg_dl: "", notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);

  async function livePreview(v) {
    if (!v || v <= 3) { setPreview(null); return; }
    try {
      const { data: r } = await api.post("/specialties/endocrinology/scores/hba1c",
                                          { hba1c_pct: Number(v) });
      setPreview(r);
    } catch { /* ignore */ }
  }

  async function submit(e) {
    e.preventDefault(); setSaving(true);
    try {
      await api.post("/specialties/endocrinology/hba1c", {
        date: form.date, hba1c_pct: Number(form.hba1c_pct),
        fasting_glucose_mg_dl: form.fasting_glucose_mg_dl
          ? Number(form.fasting_glucose_mg_dl) : null,
        notes: form.notes || null,
      });
      toast.success(`HbA1c ${form.hba1c_pct}% recorded`);
      setForm({ ...form, hba1c_pct: "", fasting_glucose_mg_dl: "", notes: "" });
      setPreview(null);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  }

  const chart = useMemo(() => (data.readings || []).slice().reverse().map((r) => ({
    date: r.date?.slice(5), hba1c: r.hba1c_pct,
  })), [data]);

  return (
    <div className="space-y-6" data-testid="endo-hba1c-tab">
      <form onSubmit={submit} className="ros-card space-y-3" data-testid="a1c-form">
        <div className="font-heading text-lg">Record HbA1c</div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Field label="Date"><input type="date" value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
            data-testid="a1c-date" className="ros-input" required /></Field>
          <Field label="HbA1c (%)"><input type="number" step="0.1" min="3" max="20"
            value={form.hba1c_pct}
            onChange={(e) => { setForm({ ...form, hba1c_pct: e.target.value }); livePreview(e.target.value); }}
            data-testid="a1c-value" className="ros-input" required /></Field>
          <Field label="Fasting glucose (mg/dL)"><input type="number"
            value={form.fasting_glucose_mg_dl}
            onChange={(e) => setForm({ ...form, fasting_glucose_mg_dl: e.target.value })}
            data-testid="a1c-fpg" className="ros-input" /></Field>
          <Field label="Notes"><input type="text" value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            data-testid="a1c-notes" className="ros-input" placeholder="Optional" /></Field>
        </div>
        {preview && (
          <div className="rounded-2xl p-3" data-testid="a1c-preview"
               style={{ background: `${BAND_COLOR[preview.band] || "#A3ACA7"}12`,
                        border: `1px solid ${BAND_COLOR[preview.band] || "#A3ACA7"}40` }}>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-lg font-heading font-bold"
                     style={{ color: BAND_COLOR[preview.band] || "#fff" }}>
                  {preview.label}
                </div>
                <div className="text-xs text-[#A3ACA7]">eAG ≈ {preview.eag_mg_dl} mg/dL</div>
              </div>
              <div className="text-sm text-white flex-1 ml-4">{preview.recommendation}</div>
            </div>
          </div>
        )}
        <button type="submit" disabled={saving} data-testid="a1c-submit"
                className="flex items-center gap-2 px-5 py-2 rounded-full text-black font-semibold disabled:opacity-50"
                style={{ background: ACCENT }}>
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          Record HbA1c
        </button>
      </form>

      {chart.length >= 2 && (
        <div className="ros-card" data-testid="a1c-chart">
          <div className="font-heading text-lg mb-3">HbA1c trajectory</div>
          <div className="h-56">
            <ResponsiveContainer width="99%" height="100%">
              <LineChart data={chart} margin={{ top: 10, right: 16, left: -12, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#272E2B" />
                <XAxis dataKey="date" stroke="#A3ACA7" fontSize={11} />
                <YAxis stroke="#A3ACA7" fontSize={11} domain={[4, 12]} />
                <ReferenceLine y={7} stroke="#4ADE80" strokeDasharray="4 4"
                               label={{ value: "ADA target <7%", fontSize: 10, fill: "#4ADE80" }} />
                <Tooltip contentStyle={{ background: "#0F1312", border: "1px solid #272E2B", borderRadius: 12 }} />
                <Line type="monotone" dataKey="hba1c" stroke={ACCENT} strokeWidth={2} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <HistoryTable
        title={`HbA1c history (${(data.readings || []).length})`}
        testId="a1c-history"
        rows={data.readings || []}
        empty="No HbA1c readings."
        columns={[
          { label: "Date", render: (r) => r.date },
          { label: "HbA1c", render: (r) => <span className="font-mono">{r.hba1c_pct}%</span> },
          { label: "eAG", render: (r) => <span className="font-mono text-[#A3ACA7]">{r.eag_mg_dl} mg/dL</span> },
          { label: "Band", render: (r) => (
            <span className="text-xs font-bold px-2 py-0.5 rounded-full"
                  style={{ color: BAND_COLOR[r.band] || "#A3ACA7",
                           background: `${BAND_COLOR[r.band] || "#A3ACA7"}18`,
                           border: `1px solid ${BAND_COLOR[r.band] || "#A3ACA7"}40` }}>
              {r.band}
            </span>
          )},
        ]}
      />
    </div>
  );
}

/* ============ Thyroid ============ */
function ThyroidTab({ data, onSaved }) {
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    tsh_miu_l: "", free_t4_ng_dl: "", on_levothyroxine: false, dose_mcg: "",
  });
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);

  async function livePreview(v) {
    if (!v) { setPreview(null); return; }
    try {
      const { data: r } = await api.post("/specialties/endocrinology/scores/tsh",
                                          { tsh_miu_l: Number(v) });
      setPreview(r);
    } catch { /* ignore */ }
  }

  async function submit(e) {
    e.preventDefault(); setSaving(true);
    try {
      await api.post("/specialties/endocrinology/tsh", {
        date: form.date, tsh_miu_l: Number(form.tsh_miu_l),
        free_t4_ng_dl: form.free_t4_ng_dl ? Number(form.free_t4_ng_dl) : null,
        on_levothyroxine: form.on_levothyroxine,
        dose_mcg: form.dose_mcg ? Number(form.dose_mcg) : null,
      });
      toast.success(`TSH ${form.tsh_miu_l} mIU/L recorded`);
      setForm({ ...form, tsh_miu_l: "", free_t4_ng_dl: "", dose_mcg: "" });
      setPreview(null);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  }

  return (
    <div className="space-y-6" data-testid="endo-thyroid-tab">
      <form onSubmit={submit} className="ros-card space-y-3" data-testid="tsh-form">
        <div className="font-heading text-lg">Record TSH</div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Field label="Date"><input type="date" value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
            data-testid="tsh-date" className="ros-input" required /></Field>
          <Field label="TSH (mIU/L)"><input type="number" step="0.01" value={form.tsh_miu_l}
            onChange={(e) => { setForm({ ...form, tsh_miu_l: e.target.value }); livePreview(e.target.value); }}
            data-testid="tsh-value" className="ros-input" required /></Field>
          <Field label="Free T4 (ng/dL)"><input type="number" step="0.01" value={form.free_t4_ng_dl}
            onChange={(e) => setForm({ ...form, free_t4_ng_dl: e.target.value })}
            data-testid="tsh-ft4" className="ros-input" /></Field>
          <Field label="Levo dose (µg)"><input type="number" value={form.dose_mcg}
            onChange={(e) => setForm({ ...form, dose_mcg: e.target.value })}
            data-testid="tsh-dose" className="ros-input" disabled={!form.on_levothyroxine} /></Field>
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={form.on_levothyroxine}
                 onChange={(e) => setForm({ ...form, on_levothyroxine: e.target.checked })}
                 data-testid="tsh-on-levo"
                 className="rounded" />
          On levothyroxine
        </label>
        {preview && (
          <div className="rounded-2xl p-3" data-testid="tsh-preview"
               style={{ background: `${BAND_COLOR[preview.band] || "#A3ACA7"}12`,
                        border: `1px solid ${BAND_COLOR[preview.band] || "#A3ACA7"}40` }}>
            <div className="text-lg font-heading font-bold"
                 style={{ color: BAND_COLOR[preview.band] || "#fff" }}>{preview.band}</div>
            <div className="text-sm text-white mt-1">{preview.recommendation}</div>
          </div>
        )}
        <button type="submit" disabled={saving} data-testid="tsh-submit"
                className="flex items-center gap-2 px-5 py-2 rounded-full text-black font-semibold disabled:opacity-50"
                style={{ background: ACCENT }}>
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          Record TSH
        </button>
      </form>

      <HistoryTable
        title={`TSH history (${(data.readings || []).length})`}
        testId="tsh-history"
        rows={data.readings || []}
        empty="No TSH readings."
        columns={[
          { label: "Date", render: (r) => r.date },
          { label: "TSH", render: (r) => <span className="font-mono">{r.tsh_miu_l}</span> },
          { label: "fT4", render: (r) => <span className="font-mono text-[#A3ACA7]">{r.free_t4 ?? r.free_t4_ng_dl ?? "—"}</span> },
          { label: "Band", render: (r) => (
            <span className="text-xs font-bold px-2 py-0.5 rounded-full"
                  style={{ color: BAND_COLOR[r.band] || "#A3ACA7",
                           background: `${BAND_COLOR[r.band] || "#A3ACA7"}18`,
                           border: `1px solid ${BAND_COLOR[r.band] || "#A3ACA7"}40` }}>
              {r.band}
            </span>
          )},
        ]}
      />
    </div>
  );
}

/* ============ Risk Scores ============ */
function RiskTab() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <FindriscCalc />
      <DkaCalc />
    </div>
  );
}

function FindriscCalc() {
  const [form, setForm] = useState({
    age_pts: 0, bmi_pts: 0, waist_pts: 0, activity_pts: 0,
    veg_fruit_pts: 0, bp_meds_pts: 0,
    high_glucose_history_pts: 0, family_history_pts: 0,
  });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  async function compute() {
    setBusy(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, Number(v)]));
      const { data } = await api.post("/specialties/endocrinology/scores/findrisc", payload);
      setResult(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  }

  const items = [
    { key: "age_pts", label: "Age", options: [[0, "<45"], [2, "45-54"], [3, "55-64"], [4, "≥65"]] },
    { key: "bmi_pts", label: "BMI", options: [[0, "<25"], [1, "25-30"], [3, ">30"]] },
    { key: "waist_pts", label: "Waist (M/F)", options: [[0, "<94/80"], [3, "94-102/80-88"], [4, ">102/88"]] },
    { key: "activity_pts", label: "Physical activity ≥30 min/day", options: [[0, "Yes"], [2, "No"]] },
    { key: "veg_fruit_pts", label: "Veg/fruit daily", options: [[0, "Yes"], [1, "No"]] },
    { key: "bp_meds_pts", label: "BP medication", options: [[0, "No"], [2, "Yes"]] },
    { key: "high_glucose_history_pts", label: "Prior high glucose", options: [[0, "No"], [5, "Yes"]] },
    { key: "family_history_pts", label: "Family Hx diabetes", options: [[0, "None"], [3, "2nd-degree"], [5, "1st-degree"]] },
  ];

  return (
    <div className="ros-card space-y-4" data-testid="findrisc-calc">
      <div className="font-heading text-lg">FINDRISC — 10-yr T2DM risk</div>
      <div className="text-xs text-[#A3ACA7]">Lindström 2003. Score 0-26.</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.map((it) => (
          <Field key={it.key} label={it.label}>
            <select value={form[it.key]}
                    onChange={(e) => setForm({ ...form, [it.key]: Number(e.target.value) })}
                    data-testid={`findrisc-${it.key}`} className="ros-input">
              {it.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Field>
        ))}
      </div>
      <button onClick={compute} disabled={busy} data-testid="findrisc-compute"
              className="flex items-center gap-2 px-5 py-2 rounded-full text-black font-semibold disabled:opacity-50"
              style={{ background: ACCENT }}>
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Calculator size={14} />}
        Compute FINDRISC
      </button>
      {result && (
        <div className="rounded-2xl p-4" data-testid="findrisc-result"
             style={{ background: `${BAND_COLOR[result.risk_band] || "#A3ACA7"}12`,
                      border: `1px solid ${BAND_COLOR[result.risk_band] || "#A3ACA7"}40` }}>
          <div className="text-4xl font-heading font-bold"
               style={{ color: BAND_COLOR[result.risk_band] || "#fff" }}>
            {result.score}<span className="text-xl text-[#A3ACA7] ml-1">/26</span>
          </div>
          <div className="text-sm font-semibold mt-1">{result.risk_band} · risk {result.risk_10y}</div>
          <div className="text-sm text-white mt-2">{result.recommendation}</div>
        </div>
      )}
    </div>
  );
}

function DkaCalc() {
  const [form, setForm] = useState({
    glucose_mg_dl: 350, bicarbonate_meq_l: 15, anion_gap: 18,
    ketones_positive: true, ph: 7.25,
  });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  async function compute() {
    setBusy(true);
    try {
      const { data } = await api.post("/specialties/endocrinology/scores/dka", form);
      setResult(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  }

  const severityColor = {
    "Not DKA": "#A3ACA7", "Mild DKA": "#FBBF24",
    "Moderate DKA": "#FB923C", "Severe DKA": "#F87171",
  }[result?.severity] || "#A3ACA7";

  return (
    <div className="ros-card space-y-4" data-testid="dka-calc">
      <div className="font-heading text-lg">DKA Classifier (ADA/Alberti)</div>
      <div className="text-xs text-[#A3ACA7]">Glucose &gt;250 · Bicarb &lt;18 or AG &gt;12 · Ketones +ve</div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Glucose (mg/dL)"><input type="number" value={form.glucose_mg_dl}
          onChange={(e) => setForm({ ...form, glucose_mg_dl: Number(e.target.value) })}
          data-testid="dka-glucose" className="ros-input" /></Field>
        <Field label="Bicarbonate (mEq/L)"><input type="number" value={form.bicarbonate_meq_l}
          onChange={(e) => setForm({ ...form, bicarbonate_meq_l: Number(e.target.value) })}
          data-testid="dka-bicarb" className="ros-input" /></Field>
        <Field label="Anion gap"><input type="number" value={form.anion_gap}
          onChange={(e) => setForm({ ...form, anion_gap: Number(e.target.value) })}
          data-testid="dka-ag" className="ros-input" /></Field>
        <Field label="pH"><input type="number" step="0.01" value={form.ph}
          onChange={(e) => setForm({ ...form, ph: Number(e.target.value) })}
          data-testid="dka-ph" className="ros-input" /></Field>
      </div>
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input type="checkbox" checked={form.ketones_positive}
               onChange={(e) => setForm({ ...form, ketones_positive: e.target.checked })}
               data-testid="dka-ketones" className="rounded" />
        Ketones positive (urine or serum)
      </label>
      <button onClick={compute} disabled={busy} data-testid="dka-compute"
              className="flex items-center gap-2 px-5 py-2 rounded-full text-black font-semibold disabled:opacity-50"
              style={{ background: ACCENT }}>
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Calculator size={14} />}
        Classify DKA
      </button>
      {result && (
        <div className="rounded-2xl p-4" data-testid="dka-result"
             style={{ background: `${severityColor}12`,
                      border: `1px solid ${severityColor}40` }}>
          <div className="text-2xl font-heading font-bold" style={{ color: severityColor }}>
            {result.severity}
          </div>
          <div className="text-xs text-[#A3ACA7] mt-1">
            {Object.entries(result.criteria).map(([k, v]) => (
              <div key={k}>{v ? "✓" : "✗"} {k.replace(/_/g, " ")}</div>
            ))}
          </div>
          <div className="text-sm text-white mt-2">{result.recommendation}</div>
        </div>
      )}
    </div>
  );
}

/* ============ Digital Twin — Hypoglycaemia projection ============ */
function TwinTab() {
  const [form, setForm] = useState({
    current_glucose_mg_dl: 95, slope_mg_dl_per_10min: -3,
    on_basal_insulin: true, recent_bolus_units: 0,
  });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  async function compute() {
    setBusy(true);
    try {
      const { data } = await api.post("/specialties/endocrinology/twin/hypoglycaemia", form);
      setResult(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  }

  const riskColor = {
    critical: "#F87171", high: "#FB923C",
    moderate: "#FBBF24", low: "#4ADE80",
  }[result?.priority] || "#A3ACA7";

  const chart = result ? [
    { t: "Now", glucose: result.current_mg_dl },
    { t: "+30 min", glucose: result.projected_30min },
    { t: "+60 min", glucose: result.projected_60min },
  ] : null;

  return (
    <div className="ros-card space-y-4" data-testid="endo-twin-tab">
      <div>
        <div className="font-heading text-lg">Hypoglycaemia Digital Twin</div>
        <div className="text-xs text-[#A3ACA7] mt-0.5">
          Projects 30 and 60-min glucose from current + trend + insulin-on-board.
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Field label="Current glucose (mg/dL)"><input type="number" value={form.current_glucose_mg_dl}
          onChange={(e) => setForm({ ...form, current_glucose_mg_dl: Number(e.target.value) })}
          data-testid="twin-current" className="ros-input" /></Field>
        <Field label="Slope (mg/dL per 10 min)"><input type="number" step="0.1" value={form.slope_mg_dl_per_10min}
          onChange={(e) => setForm({ ...form, slope_mg_dl_per_10min: Number(e.target.value) })}
          data-testid="twin-slope" className="ros-input" /></Field>
        <Field label="Recent bolus (U)"><input type="number" step="0.5" value={form.recent_bolus_units}
          onChange={(e) => setForm({ ...form, recent_bolus_units: Number(e.target.value) })}
          data-testid="twin-bolus" className="ros-input" /></Field>
        <Field label="On basal insulin?"><select value={form.on_basal_insulin ? "yes" : "no"}
          onChange={(e) => setForm({ ...form, on_basal_insulin: e.target.value === "yes" })}
          data-testid="twin-basal" className="ros-input">
          <option value="yes">Yes</option><option value="no">No</option>
        </select></Field>
      </div>
      <button onClick={compute} disabled={busy} data-testid="twin-compute"
              className="flex items-center gap-2 px-5 py-2 rounded-full text-black font-semibold disabled:opacity-50"
              style={{ background: ACCENT }}>
        {busy ? <Loader2 size={14} className="animate-spin" /> : <TrendingUp size={14} />}
        Project hypo risk
      </button>
      {result && (
        <div className="space-y-4" data-testid="twin-result">
          <div className="rounded-2xl p-4"
               style={{ background: `${riskColor}12`,
                        border: `1px solid ${riskColor}40` }}>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-heading font-bold" style={{ color: riskColor }}>
                  {result.risk_band}
                </div>
                <div className="text-xs text-[#A3ACA7] mt-1">
                  min projected {result.min_projected} mg/dL over next hour
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-[#A3ACA7]">30 min → {result.projected_30min}</div>
                <div className="text-xs text-[#A3ACA7]">60 min → {result.projected_60min}</div>
              </div>
            </div>
            <div className="text-sm text-white mt-2">{result.recommendation}</div>
          </div>
          {chart && (
            <div className="ros-card">
              <div className="h-48">
                <ResponsiveContainer width="99%" height="100%">
                  <LineChart data={chart} margin={{ top: 10, right: 16, left: -12, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#272E2B" />
                    <XAxis dataKey="t" stroke="#A3ACA7" fontSize={11} />
                    <YAxis stroke="#A3ACA7" fontSize={11} domain={[0, 200]} />
                    <ReferenceLine y={70} stroke="#F87171" strokeDasharray="4 4"
                                   label={{ value: "Hypo <70", fontSize: 10, fill: "#F87171" }} />
                    <Tooltip contentStyle={{ background: "#0F1312", border: "1px solid #272E2B", borderRadius: 12 }} />
                    <Line type="monotone" dataKey="glucose" stroke={ACCENT} strokeWidth={2} dot={{ r: 5 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ============ Formulary + Care Plans ============ */
function FormularyTab({ items }) {
  return (
    <div className="space-y-3" data-testid="endo-formulary-tab">
      <div className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">
        Diabetes formulary — ADA 2026 ladder
      </div>
      {items.map((it) => (
        <div key={it.drug} className="ros-card" data-testid={`endo-med-${it.drug.replace(/\s+/g, "-").toLowerCase()}`}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="font-heading text-base">{it.drug}</div>
              <div className="text-[10px] uppercase tracking-wider text-[#7A8480] mt-0.5">
                {it.class} · Line {it.line}
              </div>
              <div className="text-sm text-[#A3ACA7] mt-2">{it.typical_dose}</div>
            </div>
            <div className="text-right">
              <div className="text-xs" style={{ color: ACCENT }}>{it.a1c_reduction}</div>
              <div className="text-[10px] uppercase tracking-wider text-[#A3ACA7] mt-0.5">A1c drop</div>
            </div>
          </div>
          <div className="text-xs text-[#A3ACA7] mt-2 italic">{it.notes}</div>
        </div>
      ))}
    </div>
  );
}

function CarePlansTab({ plans }) {
  return (
    <div className="space-y-4" data-testid="endo-careplans-tab">
      {plans.map((p) => (
        <div key={p.id} className="ros-card" data-testid={`endo-plan-${p.id}`}>
          <div className="flex items-center justify-between mb-3">
            <div className="font-heading text-lg">{p.title}</div>
            <div className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">
              {p.duration_weeks} weeks
            </div>
          </div>
          <div className="space-y-2">
            {p.steps.map((s, i) => (
              <div key={i} className="flex gap-3 text-sm">
                <div className="w-16 shrink-0 text-[10px] uppercase tracking-wider text-[#7A8480] pt-1">
                  Week {s.week}
                </div>
                <div className="text-white flex-1">{s.action}</div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ============ Shared History Table ============ */
function HistoryTable({ title, testId, rows, columns, empty }) {
  return (
    <div className="ros-card" data-testid={testId}>
      <div className="font-heading text-lg mb-3">{title}</div>
      {rows.length === 0 ? (
        <div className="text-sm text-[#7A8480]">{empty}</div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.label} className="text-left text-[10px] uppercase tracking-wider text-[#A3ACA7] pb-2">
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-[#272E2B]">
                {columns.map((c) => (
                  <td key={c.label} className="py-2 pr-3">{c.render(r)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
